import React, { useState } from 'react';
import { X, Link2, Download, Check, Share2 } from 'lucide-react';

/* ─── Inline Instagram SVG (not in lucide-react) ─── */
const IgIcon = ({ size = 20, className = '' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size} className={className}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const fmt = (p) => `R${Number(p).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/* ─── Wrap text for canvas ─── */
function wrapCanvasText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let current = '';
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines.slice(0, 2);
}

/* ─── Generate branded 1080x1350 Instagram card ─── */
async function generateShareImage(product, imageUrl) {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1350;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#0A0A0A';
  ctx.fillRect(0, 0, 1080, 1350);

  /* Try drawing product image */
  let imageDrawn = false;
  if (imageUrl) {
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.crossOrigin = 'anonymous';
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = imageUrl;
      });
      const targetH = 980;
      const aspect = img.naturalWidth / img.naturalHeight;
      let dw, dh, dx, dy;
      if (aspect > 1080 / targetH) {
        dh = targetH; dw = dh * aspect;
        dx = (1080 - dw) / 2; dy = 0;
      } else {
        dw = 1080; dh = dw / aspect;
        dx = 0; dy = Math.min(0, (targetH - dh) / 2);
      }
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, 1080, targetH);
      ctx.clip();
      ctx.drawImage(img, dx, dy, dw, dh);
      ctx.restore();
      imageDrawn = true;
    } catch { imageDrawn = false; }
  }

  if (!imageDrawn) {
    /* Fallback: elegant dark gradient with gold geometry */
    const g = ctx.createLinearGradient(0, 0, 1080, 980);
    g.addColorStop(0, '#1C1C1C'); g.addColorStop(1, '#0A0A0A');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 1080, 980);
    ctx.strokeStyle = 'rgba(212,175,55,0.10)';
    ctx.lineWidth = 1;
    for (let i = 1; i <= 9; i++) {
      const s = i * 85;
      ctx.strokeRect(540 - s / 2, 490 - s / 2, s, s);
    }
    ctx.fillStyle = 'rgba(212,175,55,0.05)';
    ctx.font = 'bold 480px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.fillText('H', 540, 720);
  }

  /* Bottom gradient overlay */
  const g2 = ctx.createLinearGradient(0, 680, 0, 1350);
  g2.addColorStop(0, 'rgba(10,10,10,0)');
  g2.addColorStop(0.28, 'rgba(10,10,10,0.78)');
  g2.addColorStop(0.55, 'rgba(10,10,10,0.97)');
  g2.addColorStop(1, 'rgba(10,10,10,1)');
  ctx.fillStyle = g2;
  ctx.fillRect(0, 680, 1080, 670);

  /* Gold rule */
  ctx.strokeStyle = '#D4AF37';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(120, 1024); ctx.lineTo(960, 1024); ctx.stroke();

  /* HEIR LUXX label */
  ctx.fillStyle = '#D4AF37';
  ctx.font = 'bold 28px Georgia, serif';
  ctx.textAlign = 'center';
  ctx.fillText('— HEIR LUXX —', 540, 992);

  /* Designer */
  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 58px Georgia, serif';
  ctx.fillText(product.designer.toUpperCase(), 540, 1082);

  /* Product name */
  ctx.fillStyle = 'rgba(255,255,255,0.68)';
  ctx.font = '34px Arial, sans-serif';
  const lines = wrapCanvasText(ctx, product.name, 860);
  lines.forEach((line, i) => ctx.fillText(line, 540, 1140 + i * 50));

  /* Price */
  ctx.fillStyle = '#D4AF37';
  ctx.font = 'bold 70px Arial, sans-serif';
  ctx.fillText(fmt(product.final_price), 540, 1140 + lines.length * 50 + 76);

  /* Website */
  ctx.fillStyle = 'rgba(255,255,255,0.28)';
  ctx.font = '26px Arial, sans-serif';
  ctx.fillText('heirluxx.com', 540, 1316);

  return canvas;
}

/* ═══════════════════════════════════════════════════
   ShareModal — main component
═══════════════════════════════════════════════════ */
export function ShareModal({ product, onClose }) {
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(null); // index or 'all'
  const [shared, setShared] = useState(false);

  const images = (product.images || []).filter(Boolean);
  const productUrl = `${window.location.origin}/product/${product.product_id}`;
  // Crawler-friendly link: serves per-product OG tags, then forwards visitors to the product page
  const shareUrl = `${window.location.origin}/api/og/product/${product.product_id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const downloadImage = async (imgUrl, idx) => {
    setGenerating(idx);
    try {
      const canvas = await generateShareImage(product, imgUrl);
      const a = document.createElement('a');
      a.download = `heirluxx-${product.product_id}-${idx + 1}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
    } finally { setGenerating(null); }
  };

  const downloadAll = async () => {
    setGenerating('all');
    for (let i = 0; i < images.length; i++) {
      await downloadImage(images[i], i);
      if (i < images.length - 1) await new Promise(r => setTimeout(r, 400));
    }
    setGenerating(null);
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${product.designer} — ${product.name} | HEIR LUXX`,
          text: `Discover ${product.designer} ${product.name} for ${fmt(product.final_price)} on HEIR LUXX.`,
          url: shareUrl,
        });
        setShared(true);
      } catch { /* cancelled */ }
    } else {
      copyLink();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-[660px] max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="share-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-3">
            <IgIcon size={20} className="text-[#0A0A0A]" />
            <h2 className="font-playfair text-lg text-[#0A0A0A]">Share on Instagram</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-[#F5F5F5] transition-colors"
            data-testid="share-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col sm:flex-row gap-8">

          {/* ── Left: Post preview ── */}
          <div className="flex-shrink-0 w-full sm:w-[200px]">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#AAAAAA] font-work mb-3">Post Preview</p>

            {/* Phone-style card */}
            <div className="bg-[#0A0A0A] relative w-full" style={{ paddingTop: '125%' }}>
              <div className="absolute inset-0 overflow-hidden">
                {images[0] && (
                  <img
                    src={images[0]}
                    alt={product.name}
                    className="w-full h-[75%] object-cover"
                  />
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent pt-10 pb-3 px-3">
                  <p className="text-[#D4AF37] text-[7px] font-bold uppercase tracking-widest font-work">HEIR LUXX</p>
                  <p className="text-white text-[10px] font-bold font-work leading-tight mt-0.5">{product.designer}</p>
                  <p className="text-white/60 text-[8px] font-work leading-tight line-clamp-1 mt-0.5">{product.name}</p>
                  <p className="text-[#D4AF37] text-[11px] font-bold font-work mt-1">{fmt(product.final_price)}</p>
                  <p className="text-white/25 text-[7px] font-work mt-1">heirluxx.com</p>
                </div>
              </div>
            </div>

            {/* Carousel dots */}
            {images.length > 1 && (
              <div className="flex justify-center gap-1 mt-2">
                {images.slice(0, 5).map((_, i) => (
                  <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-[#D4AF37]' : 'bg-[#D5D5D5]'}`} />
                ))}
              </div>
            )}

            {/* All product image thumbnails (carousel images for feed) */}
            {images.length > 1 && (
              <>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#AAAAAA] font-work mt-4 mb-2">Feed Images</p>
                <div className="grid grid-cols-4 gap-1">
                  {images.slice(0, 8).map((img, i) => (
                    <div
                      key={i}
                      className="relative aspect-square bg-[#F5F5F5] overflow-hidden group cursor-pointer"
                      onClick={() => downloadImage(img, i)}
                      title={`Download image ${i + 1}`}
                    >
                      <img src={img} alt={`Image ${i + 1}`} className="w-full h-full object-cover group-hover:opacity-70 transition-opacity" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        {generating === i
                          ? <span className="text-[7px] text-white font-work">...</span>
                          : <Download size={10} className="text-white drop-shadow" />
                        }
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── Right: Actions ── */}
          <div className="flex-1 flex flex-col gap-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#AAAAAA] font-work mb-4">Share Options</p>

              {/* Link preview */}
              <div className="bg-[#F9F9F9] border border-[#EBEBEB] p-3 mb-5">
                <p className="text-[10px] text-[#AAAAAA] font-work mb-1">Product Link</p>
                <p className="text-xs text-[#0A0A0A] font-work truncate" data-testid="share-product-url">{productUrl}</p>
                <p className="text-[9px] text-[#AAAAAA] font-work mt-1.5 leading-relaxed">
                  Copied links use a preview-ready version that shows the product image, name and price in Instagram, WhatsApp and Facebook.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                {/* Instagram / Web Share */}
                <button
                  onClick={shareNative}
                  className="flex items-center gap-3 w-full bg-[#0A0A0A] text-white px-4 py-3.5 hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors group"
                  data-testid="share-instagram-btn"
                >
                  <IgIcon size={16} />
                  <span className="text-xs font-bold font-work uppercase tracking-wider">
                    {shared ? 'Shared!' : 'Share to Instagram'}
                  </span>
                </button>

                {/* Download first image (branded card) */}
                <button
                  onClick={() => downloadImage(images[0], 0)}
                  disabled={generating === 0}
                  className="flex items-center gap-3 w-full border border-[#0A0A0A] text-[#0A0A0A] px-4 py-3.5 hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-50"
                  data-testid="download-share-image-btn"
                >
                  <Download size={15} />
                  <span className="text-xs font-bold font-work uppercase tracking-wider">
                    {generating === 0 ? 'Generating…' : 'Download Post Image'}
                  </span>
                </button>

                {/* Download ALL images (carousel) */}
                {images.length > 1 && (
                  <button
                    onClick={downloadAll}
                    disabled={generating === 'all'}
                    className="flex items-center gap-3 w-full border border-[#CCCCCC] text-[#555555] px-4 py-3.5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-50"
                    data-testid="download-all-images-btn"
                  >
                    <Download size={15} />
                    <span className="text-xs font-bold font-work uppercase tracking-wider">
                      {generating === 'all' ? 'Downloading…' : `Download All ${images.length} Images`}
                    </span>
                  </button>
                )}

                {/* Copy link */}
                <button
                  onClick={copyLink}
                  className="flex items-center gap-3 w-full border border-[#EBEBEB] text-[#555555] px-4 py-3.5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                  data-testid="copy-product-link-btn"
                >
                  {copied ? <Check size={15} className="text-green-600" /> : <Link2 size={15} />}
                  <span className="text-xs font-bold font-work uppercase tracking-wider">
                    {copied ? 'Link Copied!' : 'Copy Product Link'}
                  </span>
                </button>
              </div>
            </div>

            {/* How-to tip */}
            <div className="border-t border-[#F0F0F0] pt-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-work mb-2">How to Post</p>
              <ol className="text-[11px] text-[#888888] font-work leading-relaxed space-y-1 list-decimal list-inside">
                <li>Download the post image(s) above</li>
                <li>Open Instagram → tap <strong>+</strong> → select the image(s)</li>
                <li>Add the product link in your caption or bio</li>
                <li>Tag <strong>@heirluxx</strong> so we can repost you</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
